import { DataTable } from "@/components/data-table/data-table";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { useDataTable } from "@/hooks/use-data-table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { ColumnDef } from "@tanstack/react-table";
import { Filter, Info, PlaneLanding, PlaneTakeoff } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";

// Types matching the proposed MarketItemSchema
export interface MarketItem {
  market_code: string;
  market_name: string;
  direction_context: "Departure" | "Arrival" | "All";
  domestic_international: "Domestic" | "International";
  num_flights: number;
  predicted_passengers: number;
  percent_scoped_pax: number;
  percent_scoped_flights: number;
  avg_pax_per_flight: string | number;
  next_flight_time: string | null;
  relevant_gates: string[];
  dep_pax?: number;
  arr_pax?: number;
  dep_flights?: number;
  arr_flights?: number;
  // Opportunity Window fields
  next_flight_passengers?: number | null;
  boarding_time?: string | null;
  opportunity_window_start?: string | null;
  opportunity_window_end?: string | null;
  window_status?: string | null;
  next_flight_gate?: string | null;
}

/** Convert a HH:MM 24-hour string to 12-hour AM/PM display */
const formatTime = (t: string | null | undefined): string => {
  if (!t) return "-";
  const [hStr, mStr] = t.split(":");
  let h = parseInt(hStr, 10);
  const m = mStr ?? "00";
  const suffix = h >= 12 ? "PM" : "AM";
  if (h > 12) h -= 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${suffix}`;
};

type WindowStatus =
  | "Upcoming"
  | "Active"
  | "Last Call"
  | "Boarding"
  | "Closed"
  | string;

const statusConfig: Record<
  string,
  { bg: string; text: string; border: string; pulse?: boolean }
> = {
  Upcoming: {
    bg: "bg-slate-700/60",
    text: "text-slate-300",
    border: "border-slate-600/50",
  },
  Active: {
    bg: "bg-green-500/20",
    text: "text-green-300",
    border: "border-green-500/40",
    pulse: true,
  },
  "Last Call": {
    bg: "bg-amber-500/20",
    text: "text-amber-300",
    border: "border-amber-500/40",
  },
  Boarding: {
    bg: "bg-orange-500/20",
    text: "text-orange-300",
    border: "border-orange-500/40",
  },
  Closed: {
    bg: "bg-red-900/30",
    text: "text-red-400",
    border: "border-red-700/40",
  },
};

const WindowStatusBadge: React.FC<{
  status: WindowStatus | null | undefined;
}> = ({ status }) => {
  if (!status) return <span className="text-slate-600">-</span>;
  const cfg = statusConfig[status] ?? statusConfig["Upcoming"];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${cfg.bg} ${cfg.text} ${cfg.border}`}
    >
      {cfg.pulse && (
        <span className="relative flex h-1.5 w-1.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500" />
        </span>
      )}
      {status}
    </span>
  );
};

type SortConfig = {
  key: keyof MarketItem;
  direction: "asc" | "desc";
};

type DirectionFilter = "All" | "Departures" | "Arrivals";

const DEFAULT_DIRECTION_FILTER: DirectionFilter = "Departures";

export const MarketDemand: React.FC<{
  data: MarketItem[];
  /** Identifies the selected store/location; changing it resets the view. */
  storeId?: number | null;
}> = ({ data, storeId = null }) => {
  const [directionFilter, setDirectionFilter] = useState<DirectionFilter>(
    DEFAULT_DIRECTION_FILTER
  );

  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: "predicted_passengers",
    direction: "desc",
  });

  // Selecting or changing the store/service area returns the view to Departures.
  useEffect(() => {
    setDirectionFilter(DEFAULT_DIRECTION_FILTER);
  }, [storeId]);

  const handleSort = (key: keyof MarketItem) => {
    setSortConfig((currentSort) => {
      if (currentSort.key !== key) {
        return { key, direction: "desc" };
      }
      return {
        key,
        direction: currentSort.direction === "asc" ? "desc" : "asc",
      };
    });
  };

  let displayData: MarketItem[] = [];

  if (directionFilter === "All") {
    const grouped = new Map<string, MarketItem>();
    data.forEach((item) => {
      if (!grouped.has(item.market_code)) {
        grouped.set(item.market_code, {
          ...item,
          direction_context: "All",
          dep_pax: 0,
          arr_pax: 0,
          dep_flights: 0,
          arr_flights: 0,
          // percent values are intentionally 0 here — recalculated after all items are grouped
          percent_scoped_pax: 0,
          percent_scoped_flights: 0,
          num_flights: 0,
          predicted_passengers: 0,
          relevant_gates: [],
          // Opportunity window fields reset — carried in below
          next_flight_passengers: null,
          boarding_time: null,
          opportunity_window_start: null,
          opportunity_window_end: null,
          window_status: null,
          next_flight_gate: null,
        });
      }
      const g = grouped.get(item.market_code)!;
      g.predicted_passengers += item.predicted_passengers;
      g.num_flights += item.num_flights;
      // NOTE: Do NOT sum percent_scoped_pax/flights here — those are direction-scoped
      // percentages (dep share vs all deps; arr share vs all arrs). Summing them produces
      // wrong numbers. We recalculate from combined totals after the loop instead.

      const gatesSet = new Set([...g.relevant_gates, ...item.relevant_gates]);
      g.relevant_gates = Array.from(gatesSet);

      if (item.direction_context === "Departure") {
        g.dep_pax = (g.dep_pax || 0) + item.predicted_passengers;
        g.dep_flights = (g.dep_flights || 0) + item.num_flights;
      } else {
        g.arr_pax = (g.arr_pax || 0) + item.predicted_passengers;
        g.arr_flights = (g.arr_flights || 0) + item.num_flights;
      }

      g.avg_pax_per_flight =
        g.num_flights > 0
          ? Math.round(g.predicted_passengers / g.num_flights)
          : 0;

      if (item.next_flight_time) {
        if (!g.next_flight_time) g.next_flight_time = item.next_flight_time;
        else if (item.next_flight_time < g.next_flight_time)
          g.next_flight_time = item.next_flight_time;
      }

      // Carry opportunity window fields:
      // Departure window is the primary concession signal (pre-boarding).
      // If a departure row exists, use its window. Fall back to arrival window
      // if no departure data is present for this market.
      const isDepRow = item.direction_context === "Departure";
      const hasDepWindow =
        isDepRow && (item.opportunity_window_start || item.boarding_time);
      const hasArrWindow = !isDepRow && item.opportunity_window_start;

      // Prefer departure window; only apply arrival window if no dep window set yet
      if (hasDepWindow) {
        g.next_flight_passengers = item.next_flight_passengers ?? null;
        g.boarding_time = item.boarding_time ?? null;
        g.opportunity_window_start = item.opportunity_window_start ?? null;
        g.opportunity_window_end = item.opportunity_window_end ?? null;
        g.window_status = item.window_status ?? null;
        g.next_flight_gate = item.next_flight_gate ?? null;
      } else if (hasArrWindow && !g.opportunity_window_start) {
        g.next_flight_passengers = item.next_flight_passengers ?? null;
        g.opportunity_window_start = item.opportunity_window_start ?? null;
        g.opportunity_window_end = item.opportunity_window_end ?? null;
        g.window_status = item.window_status ?? null;
        g.next_flight_gate = item.next_flight_gate ?? null;
      }
    });

    // Recalculate percent_scoped_pax and percent_scoped_flights using combined totals.
    // Each grouped market row's share = market_total_pax / sum_of_all_markets_pax * 100.
    const totalCombinedPax = Array.from(grouped.values()).reduce(
      (acc, g) => acc + g.predicted_passengers,
      0
    );
    const totalCombinedFlights = Array.from(grouped.values()).reduce(
      (acc, g) => acc + g.num_flights,
      0
    );
    grouped.forEach((g) => {
      g.percent_scoped_pax =
        totalCombinedPax > 0
          ? Math.round((g.predicted_passengers / totalCombinedPax) * 1000) / 10 // 1 decimal
          : 0;
      g.percent_scoped_flights =
        totalCombinedFlights > 0
          ? Math.round((g.num_flights / totalCombinedFlights) * 1000) / 10
          : 0;
    });

    displayData = Array.from(grouped.values());
  } else {
    displayData = data.filter((item) => {
      if (
        directionFilter === "Departures" &&
        item.direction_context === "Departure"
      )
        return true;
      if (
        directionFilter === "Arrivals" &&
        item.direction_context === "Arrival"
      )
        return true;
      return false;
    });
  }

  if (statusFilter.length > 0) {
    displayData = displayData.filter(
      (item) =>
        item.window_status != null && statusFilter.includes(item.window_status)
    );
  }

  if (typeFilter.length > 0) {
    displayData = displayData.filter(
      (item) =>
        item.domestic_international != null &&
        typeFilter.includes(item.domestic_international)
    );
  }

  const sortedData = displayData.sort((a, b) => {
    let aVal = a[sortConfig.key];
    let bVal = b[sortConfig.key];

    if (sortConfig.key === "window_status") {
      const STATUS_ORDER: Record<string, number> = {
        Upcoming: 0,
        Active: 1,
        Boarding: 2,
        "Last Call": 3,
        Closed: 4,
      };
      // @ts-ignore - we know these are strings at this point
      aVal = STATUS_ORDER[aVal] ?? 99;
      // @ts-ignore
      bVal = STATUS_ORDER[bVal] ?? 99;
    }

    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;

    if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;

    // Tie-breakers
    if (sortConfig.key === "predicted_passengers") {
      if (a.num_flights !== b.num_flights) {
        return b.num_flights - a.num_flights; // Descending
      }
      return a.market_name.localeCompare(b.market_name); // Alphabetical
    }
    return 0;
  });

  const columns = useMemo<ColumnDef<MarketItem>[]>(
    () => [
      {
        id: "market_name",
        accessorKey: "market_name",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Market" />
        ),
        cell: ({ row }) => {
          const market = row.original;
          return (
            <div className="flex flex-col gap-1 min-w-[200px] max-w-[300px] whitespace-normal">
              <div className="flex flex-wrap items-center gap-2">
                <div className="font-bold text-white">{market.market_code}</div>
                {market.direction_context !== "All" && (
                  <span
                    className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider ${
                      market.direction_context === "Departure"
                        ? "bg-green-500/20 text-green-400 border border-green-500/30"
                        : "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"
                    }`}
                  >
                    {market.direction_context === "Departure" ? (
                      <PlaneTakeoff className="w-3 h-3" />
                    ) : (
                      <PlaneLanding className="w-3 h-3" />
                    )}
                    {market.direction_context}
                  </span>
                )}
              </div>
              <div className="text-[11px] text-slate-400 leading-tight">
                {market.market_name}
              </div>
            </div>
          );
        },
        enableSorting: true,
      },
      {
        id: "predicted_passengers",
        accessorKey: "predicted_passengers",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Predicted Passengers" />
        ),
        cell: ({ row }) => {
          const market = row.original;
          return (
            <div>
              <div className="text-lg font-semibold text-white">
                {market.predicted_passengers.toLocaleString()}
              </div>
              {directionFilter === "All" && (
                <div className="text-xs text-slate-400 font-medium whitespace-nowrap mt-0.5">
                  <span className="text-green-400" title="Departures">
                    Dep: {market.dep_pax?.toLocaleString() || 0}
                  </span>
                  <span className="mx-1.5 text-slate-600">|</span>
                  <span className="text-cyan-400" title="Arrivals">
                    Arr: {market.arr_pax?.toLocaleString() || 0}
                  </span>
                </div>
              )}
            </div>
          );
        },
        enableSorting: true,
      },
      {
        id: "percent_scoped_pax",
        accessorKey: "percent_scoped_pax",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Passenger Share" />
        ),
        cell: ({ row }) => {
          const market = row.original;
          return (
            <div className="flex items-center gap-2 min-w-[100px]">
              <span className="text-white">
                {market.percent_scoped_pax.toFixed(1)}%
              </span>
              <div className="w-16 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-500"
                  style={{
                    width: `${Math.min(100, market.percent_scoped_pax)}%`,
                  }}
                ></div>
              </div>
            </div>
          );
        },
        enableSorting: true,
      },
      {
        id: "num_flights",
        accessorKey: "num_flights",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Flights" />
        ),
        cell: ({ row }) => {
          const market = row.original;
          return (
            <div className="text-slate-300">
              <div className="text-base">{market.num_flights}</div>
              {directionFilter === "All" && (
                <div className="text-xs text-slate-400 font-medium whitespace-nowrap mt-1">
                  <span className="text-green-400" title="Departures">
                    {market.dep_flights || 0}
                  </span>
                  <span className="mx-1.5 text-slate-600">|</span>
                  <span className="text-cyan-400" title="Arrivals">
                    {market.arr_flights || 0}
                  </span>
                </div>
              )}
            </div>
          );
        },
        enableSorting: true,
      },
      {
        id: "percent_scoped_flights",
        accessorKey: "percent_scoped_flights",
        header: ({ column }) => (
          <div className="flex items-center gap-1">
            <DataTableColumnHeader column={column} title="Flight Share" />
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-slate-500 hover:text-slate-300 transition-colors cursor-default">
                    <Info className="w-3 h-3" />
                  </span>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  className="max-w-[220px] text-center"
                >
                  {directionFilter === "All"
                    ? "% of total flights (dep + arr) across all markets"
                    : directionFilter === "Departures"
                      ? "% of total departure flights across all markets"
                      : "% of total arrival flights across all markets"}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        ),
        cell: ({ row }) => {
          const market = row.original;
          return (
            <div className="flex items-center gap-2 min-w-[100px]">
              <span className="text-white">
                {market.percent_scoped_flights.toFixed(1)}%
              </span>
              <div className="w-16 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500"
                  style={{
                    width: `${Math.min(100, market.percent_scoped_flights)}%`,
                  }}
                ></div>
              </div>
            </div>
          );
        },
        enableSorting: true,
      },
      {
        id: "domestic_international",
        accessorKey: "domestic_international",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Type" />
        ),
        cell: ({ row }) => {
          const market = row.original;
          return (
            <span
              className={`px-2 py-1 rounded text-xs font-medium ${
                market.domestic_international === "Domestic"
                  ? "bg-blue-500/20 text-blue-300 border border-blue-500/30"
                  : "bg-purple-500/20 text-purple-300 border border-purple-500/30"
              }`}
            >
              {market.domestic_international}
            </span>
          );
        },
        enableSorting: true,
      },
      {
        id: "avg_pax_per_flight",
        accessorKey: "avg_pax_per_flight",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Avg Pax / Flight" />
        ),
        cell: ({ row }) => {
          const market = row.original;
          return (
            <div className="text-slate-300">
              {market.num_flights === 0 ? "N/A" : market.avg_pax_per_flight}
            </div>
          );
        },
        enableSorting: true,
      },
      {
        id: "next_flight_time",
        accessorKey: "next_flight_time",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Next Flight" />
        ),
        cell: ({ row }) => {
          const market = row.original;
          return market.next_flight_time ? (
            <span className="text-emerald-400 font-mono text-sm">
              {formatTime(market.next_flight_time)}
            </span>
          ) : (
            <span className="text-slate-500">-</span>
          );
        },
        enableSorting: true,
      },
      {
        id: "gates",
        accessorKey: "relevant_gates",
        size: 180,
        minSize: 160,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Gates" />
        ),
        cell: ({ row }) => {
          const market = row.original;
          if (market.relevant_gates.length === 0)
            return <span className="text-slate-600 text-xs">-</span>;
          return (
            <div className="text-xs flex flex-wrap gap-x-1.5 gap-y-0.5 min-w-[160px]">
              {market.relevant_gates.map((gate) => {
                const isActive =
                  market.next_flight_gate != null &&
                  gate === market.next_flight_gate;
                return (
                  <span
                    key={gate}
                    className={
                      isActive ? "text-amber-400 font-bold" : "text-slate-400"
                    }
                  >
                    {gate}
                  </span>
                );
              })}
            </div>
          );
        },
        enableSorting: false,
      },
      {
        id: "next_flight_passengers",
        accessorKey: "next_flight_passengers",
        size: 110,
        minSize: 100,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Next Flt Pax" />
        ),
        cell: ({ row }) => {
          const market = row.original;
          const pax = market.next_flight_passengers;
          return (
            <div className="min-w-[90px]">
              {pax != null && pax > 0 ? (
                <span className="text-slate-200 font-mono text-sm">
                  {pax.toLocaleString()}
                </span>
              ) : (
                <span className="text-slate-600">-</span>
              )}
            </div>
          );
        },
        enableSorting: true,
      },
      ...(directionFilter !== "Arrivals"
        ? [
            {
              id: "boarding_time",
              accessorKey: "boarding_time",
              header: ({ column }: any) => (
                <DataTableColumnHeader column={column} title="Boarding" />
              ),
              cell: ({ row }: any) => {
                const market = row.original;
                return market.boarding_time ? (
                  <span className="text-orange-300 font-mono text-sm whitespace-nowrap">
                    {formatTime(market.boarding_time)}
                  </span>
                ) : (
                  <span className="text-slate-600">-</span>
                );
              },
              enableSorting: true,
            } as ColumnDef<MarketItem>,
          ]
        : []),
      {
        id: "opportunity_window",
        accessorKey: "opportunity_window_start",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Opportunity Window" />
        ),
        cell: ({ row }) => {
          const market = row.original;
          const s = market.opportunity_window_start;
          const e = market.opportunity_window_end;
          if (!s && !e) return <span className="text-slate-600">-</span>;
          return (
            <span className="text-indigo-300 font-mono text-xs whitespace-nowrap">
              {formatTime(s)} – {formatTime(e)}
            </span>
          );
        },
        enableSorting: false,
      },
      {
        id: "window_status",
        accessorKey: "window_status",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Status" />
        ),
        cell: ({ row }) => (
          <WindowStatusBadge status={row.original.window_status} />
        ),
        enableSorting: true,
      },
    ],
    [directionFilter]
  );

  const { table: dataTableInstance } = useDataTable({
    data: sortedData,
    columns,
    pageCount: 1,
    meta: {
      statusFilter,
      setStatusFilter,
    },
    onSortingChangeCallback: (sorting) => {
      if (sorting.length > 0) {
        const firstSort = sorting[0];
        setSortConfig({
          key: firstSort.id as keyof MarketItem,
          direction: firstSort.desc ? "desc" : "asc",
        });
      }
    },
  });

  return (
    <div className="flex flex-col w-full mt-2 mb-8">
      {/* Toggles toolbar */}
      <div className="flex justify-end items-center gap-4 mb-4">
        {/* Type Filter Dropdown */}
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-slate-400">Type</span>
          <DropdownMenu>
            <DropdownMenuTrigger
              className={`flex items-center justify-center w-6 h-6 rounded transition-colors outline-none border ${
                typeFilter.length > 0
                  ? "text-indigo-400 bg-indigo-500/20 border-indigo-500/30 hover:bg-indigo-500/30"
                  : "text-slate-400 bg-slate-800 border-slate-700 hover:bg-slate-700 hover:text-slate-200"
              }`}
            >
              <Filter className="w-3.5 h-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40 p-1">
              {["Domestic", "International"].map((type) => {
                const checked = typeFilter.includes(type);
                return (
                  <DropdownMenuCheckboxItem
                    key={type}
                    checked={checked}
                    onCheckedChange={(val) => {
                      setTypeFilter((prev) =>
                        val ? [...prev, type] : prev.filter((t) => t !== type)
                      );
                    }}
                    onSelect={(e) => e.preventDefault()}
                    className="text-xs"
                  >
                    {type}
                  </DropdownMenuCheckboxItem>
                );
              })}
              {typeFilter.length > 0 && (
                <>
                  <div className="my-1 h-px bg-slate-700" />
                  <DropdownMenuItem
                    className="text-xs text-slate-400 justify-center"
                    onSelect={() => setTypeFilter([])}
                  >
                    Clear filter
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="w-px h-5 bg-slate-700"></div>

        {/* Status Filter Dropdown */}
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-slate-400">Status</span>
          <DropdownMenu>
            <DropdownMenuTrigger
              className={`flex items-center justify-center w-6 h-6 rounded transition-colors outline-none border ${
                statusFilter.length > 0
                  ? "text-indigo-400 bg-indigo-500/20 border-indigo-500/30 hover:bg-indigo-500/30"
                  : "text-slate-400 bg-slate-800 border-slate-700 hover:bg-slate-700 hover:text-slate-200"
              }`}
            >
              <Filter className="w-3.5 h-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40 p-1">
              {["Upcoming", "Active", "Last Call", "Boarding", "Closed"].map(
                (status) => {
                  const checked = statusFilter.includes(status);
                  return (
                    <DropdownMenuCheckboxItem
                      key={status}
                      checked={checked}
                      onCheckedChange={(val) => {
                        setStatusFilter((prev) =>
                          val
                            ? [...prev, status]
                            : prev.filter((s) => s !== status)
                        );
                      }}
                      onSelect={(e) => e.preventDefault()}
                      className="text-xs"
                    >
                      {status}
                    </DropdownMenuCheckboxItem>
                  );
                }
              )}
              {statusFilter.length > 0 && (
                <>
                  <div className="my-1 h-px bg-slate-700" />
                  <DropdownMenuItem
                    className="text-xs text-slate-400 justify-center"
                    onSelect={() => setStatusFilter([])}
                  >
                    Clear filter
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="w-px h-5 bg-slate-700"></div>

        <div className="flex gap-1">
          {["All", "Departures", "Arrivals"].map((filter) => (
            <button
              key={filter}
              onClick={() => setDirectionFilter(filter as DirectionFilter)}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                directionFilter === filter
                  ? "bg-indigo-600 text-white"
                  : "bg-transparent text-slate-400 hover:bg-slate-700/50 hover:text-slate-200"
              }`}
            >
              {filter}
            </button>
          ))}
        </div>
      </div>

      <div className="w-full min-w-0 flex flex-col bg-gradient-to-b from-slate-800 to-slate-900 border border-slate-700 rounded-lg drop-shadow hover:border-indigo-500/20 transition-colors overflow-hidden">
        <div className="p-0 relative">
          <DataTable
            table={dataTableInstance}
            hidePagination
            cellClassName="py-3 px-4"
            className="[&_.relative.w-full.overflow-auto]:max-h-[65vh] [&_.relative.w-full.overflow-auto]:overflow-y-auto [&_thead]:sticky [&_thead]:top-0 [&_thead]:z-20 [&_thead]:bg-slate-800 [&_thead]:shadow-md [&_thead]:ring-1 [&_thead]:ring-slate-700 [&_th]:whitespace-nowrap"
          />
          {sortedData.length === 0 && (
            <div className="px-4 py-8 text-center text-slate-500">
              No market data available for the selected filters.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
